import * as Yup from 'yup';
import {
  INTERNAL_TICKET_UUID_PATTERN,
  TICKET_ROW_STATUS_FALLBACK_STYLE,
  TICKET_ROW_STATUS_STYLE_MAP,
  TICKET_STATUS_API_VALUES
} from './ticketManagementConstants';

/** Module-scoped pagination for ticket list (same behavior as legacy page-level state). */
export const ticketListPaginationState = {
  currentPage: 1,
  hasNextPage: true
};

export function cleanTicketDescriptionText(text = '') {
  if (!text) return '';
  let cleaned = text.trim();
  cleaned = cleaned.replace(/^(dear\s+)?(respected\s+)?sir\/?madam[:,]?\s*/i, '');
  cleaned = cleaned.replace(/^(dear\s+)?(respected\s+)?sir[:,]?\s*/i, '');
  const upiMatch = cleaned.toLowerCase().indexOf('upi');
  if (upiMatch !== -1) cleaned = cleaned.substring(0, upiMatch);
  cleaned = cleaned.replace(/CENTRAL BANK OF INDIA[\s\S]*$/i, '');
  cleaned = cleaned.replace(/The information contained[\s\S]*$/i, '');
  cleaned = cleaned
    .replace(/\r?\n|\r/g, ' ')
    .replace(/\s{2,}/g, ' ')
    .trim();
  cleaned = cleaned.replace(/(,|and)\s*$/, '').trim();
  return cleaned;
}

export function isInternalTicketUuid(value) {
  return typeof value === 'string' && INTERNAL_TICKET_UUID_PATTERN.test(value.trim());
}

export function rowFreshdeskDisplayId(row) {
  return row?.freshdeskTicketId ?? row?.freshDeskId;
}

export function findInternalUuidByFreshdeskIdInList(trimmedQuery, list) {
  if (!Array.isArray(list) || !trimmedQuery) return null;
  const queryString = String(trimmedQuery).trim();
  const row = list.find((ticket) => String(rowFreshdeskDisplayId(ticket) ?? '') === queryString);
  return row?.id ?? null;
}

export function pickTicketFromFreshdeskTicketsResponse(axiosRes) {
  const root = axiosRes?.data;
  const nested = root?.data;
  const ticket = nested?.ticket ?? nested ?? root?.ticket ?? root;
  return ticket && typeof ticket === 'object' ? ticket : null;
}

export function normalizeTicketByIdResponse(res) {
  const payload = res?.data?.data ?? res?.data;
  if (Array.isArray(payload)) return payload[0] ?? {};
  return payload ?? {};
}

export function ticketVpaForExpanded(row, detail) {
  const vpaValue =
    detail?.custom_fields?.cf_vpa ||
    detail?.merchant?.vpa ||
    detail?.vpa ||
    row?.custom_fields?.cf_vpa ||
    row?.merchant?.vpa ||
    row?.vpa;
  return vpaValue || '---';
}

export function ticketMatchesSearchQuery(ticket, query) {
  const queryText = String(query ?? '').trim();
  if (!queryText) return true;
  if (String(ticket?.id ?? '') === queryText) return true;
  const freshdeskId = rowFreshdeskDisplayId(ticket);
  if (freshdeskId === undefined || freshdeskId === null || freshdeskId === '') return false;
  return String(freshdeskId) === queryText;
}

export function getTicketRowChipStyle(status) {
  const key = String(status ?? '').toLowerCase();
  return TICKET_ROW_STATUS_STYLE_MAP[key] || TICKET_ROW_STATUS_FALLBACK_STYLE;
}

export function normalizeTicketStatusForApi(status) {
  if (status === undefined || status === null || status === '') return 'Open';
  if (typeof status === 'number') {
    const numericStatusToLabel = {
      2: 'Open',
      3: 'Pending',
      4: 'Resolved',
      5: 'Closed',
      6: 'Waiting on customer',
      7: 'Waiting on third party'
    };
    return numericStatusToLabel[status] || 'Open';
  }
  const trimmed = String(status).trim();
  const exactMatch = TICKET_STATUS_API_VALUES.find((value) => value === trimmed);
  if (exactMatch) return exactMatch;
  const lower = trimmed.toLowerCase();
  return TICKET_STATUS_API_VALUES.find((value) => value.toLowerCase() === lower) || 'Open';
}

export const addTicketFormValidationSchema = Yup.object().shape({
  subject: Yup.string().required('Subject is required'),

  description: Yup.string().when('isUpdate', {
    is: false,
    then: Yup.string().required('Description is required'),
    otherwise: Yup.string().nullable()
  }),

  email: Yup.string()
    .transform((value) => (value === '' ? null : value))
    .nullable()
    .email('Invalid email'),

  phone: Yup.string().when('isUpdate', {
    is: false,
    then: Yup.string().required('Phone number is required'),
    otherwise: Yup.string().nullable()
  }),

  complaintType: Yup.string().required('Complaint Type is required'),
  vpa: Yup.string().required('VPA is required'),
  merchantId: Yup.string().when('isUpdate', {
    is: false,
    then: (schema) => schema.required('Merchant is required — verify phone and select VPA'),
    otherwise: (schema) => schema.nullable()
  }),
  status: Yup.string().when('isUpdate', {
    is: true,
    then: (schema) =>
      schema.required('Status is required').oneOf(TICKET_STATUS_API_VALUES, 'Invalid status'),
    otherwise: (schema) => schema.nullable()
  }),
  comment: Yup.string().when('isUpdate', {
    is: true,
    then: Yup.string().required('Comment is required for updating a ticket'),
    otherwise: Yup.string().nullable()
  })
});

// --- Ticket API request/response helpers (logic lives outside network layer) ---

export function buildTicketListQueryParams(page, limit, filters = {}) {
  const params = { page, limit };

  const where =
    filters?.where && typeof filters.where === 'object' && !Array.isArray(filters.where)
      ? { ...filters.where }
      : {};

  if (filters?.status != null && filters.status !== '') {
    where.status = filters.status;
  }
  if (filters?.merchantId != null && filters.merchantId !== '') {
    where.merchantId = filters.merchantId;
  }
  if (filters?.ticketType != null && filters.ticketType !== '') {
    where.ticketType = filters.ticketType;
  }
  if (filters?.freshdeskTicketId != null && filters.freshdeskTicketId !== '') {
    const freshdeskId = filters.freshdeskTicketId;
    if (typeof freshdeskId === 'number') {
      where.freshdeskTicketId = freshdeskId;
    } else {
      const numericId = Number(freshdeskId);
      where.freshdeskTicketId =
        String(freshdeskId).trim() !== '' && Number.isFinite(numericId)
          ? numericId
          : String(freshdeskId).trim();
    }
  }

  if (Object.keys(where).length > 0) {
    params.where = JSON.stringify(where);
  }
  if (filters?.startDate) {
    params.startDate = filters.startDate;
  }
  if (filters?.endDate) {
    params.endDate = filters.endDate;
  }

  return params;
}

const listFilterLabelToApiStatus = {
  Open: 'Open',
  Pending: 'Pending',
  Resolved: 'Resolved',
  Closed: 'Closed'
};

export function buildTicketsByStatusQueryParams(
  statusLabel,
  page = 1,
  limit = 10,
  filters = {}
) {
  if (!statusLabel || statusLabel === 'All') {
    return buildTicketListQueryParams(page, limit, filters);
  }
  const apiStatus = listFilterLabelToApiStatus[statusLabel] ?? statusLabel;
  return buildTicketListQueryParams(page, limit, { ...filters, status: apiStatus });
}

export function computeHasNextTicketListPage(response, ticketsArray, pageLimit) {
  const root = response?.data;
  if (root && typeof root.hasNextPage === 'boolean') {
    return root.hasNextPage;
  }
  const nested = root?.data;
  if (
    nested &&
    typeof nested === 'object' &&
    !Array.isArray(nested) &&
    typeof nested.hasNextPage === 'boolean'
  ) {
    return nested.hasNextPage;
  }
  const itemCount = ticketsArray?.length ?? 0;
  return itemCount >= pageLimit;
}

export function parseTicketStatsPayload(response) {
  const data = response?.data;
  return data?.counts ?? data?.data?.counts ?? data?.data ?? data;
}

export function normalizeTimelineForUi(raw) {
  const payload = raw?.data?.data ?? raw?.data ?? raw;
  let list = [];
  if (Array.isArray(payload)) {
    list = payload;
  } else if (Array.isArray(payload?.items)) {
    list = payload.items;
  } else if (Array.isArray(payload?.timeline)) {
    list = payload.timeline;
  }

  return list.map((item, idx) => ({
    id: item.id ?? item._id ?? `tl-${idx}`,
    ticket_id: item.ticketId ?? item.ticket_id,
    body_text:
      item.body ?? item.body_text ?? item.message ?? item.description ?? item.comment ?? '',
    body: item.body ?? item.message ?? item.description,
    created_at: item.createdAt ?? item.created_at ?? item.timestamp ?? item.updatedAt
  }));
}

export function buildCreateTicketFormData(ticketData) {
  const formData = new FormData();
  formData.append('subject', ticketData.subject ?? '');
  formData.append('description', ticketData.description ?? '');
  formData.append('ticketScope', ticketData.ticketScope ?? 'Complain');
  formData.append('ticketType', ticketData.complaintType ?? ticketData.ticketType ?? '');
  formData.append('merchantId', ticketData.merchantId ?? '');
  formData.append('email', ticketData.email ?? '');

  (ticketData.attachments || []).forEach((file) => {
    formData.append('attachments', file);
  });
  return formData;
}

export function buildUpdateTicketFormData(updates) {
  const formData = new FormData();
  Object.entries(updates || {}).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      formData.append(key, typeof value === 'object' ? JSON.stringify(value) : String(value));
    }
  });
  return formData;
}

export function parseCreateOrUpdateTicketResponse(response) {
  return response?.data?.data ?? response?.data ?? response;
}
